import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialDocument } from "@common/types/financialDocument";
import { useRemoveFinancialDocumentMutation } from "@api/financialDocument.api";
import { getDocumentsForFinancialAccount } from "@lib/financialAccount";
import {
    financialDocumentsForFinancialAccountKeys,
    getImportStatus,
    getReconciliationStatus,
} from "../../../documents/lib";
import { ImportedDocuments } from "./ImportedDocuments/ImportedDocuments";
import styles from "./AccountFinancialDocuments.module.scss";

interface Props {
    account: FinancialAccount;
}

export const AccountFinancialDocuments: React.FC<Props> = ({ account }) => {
    const { data, isError } = useQuery({
        queryKey: financialDocumentsForFinancialAccountKeys.list(account.id),
        queryFn: () => getDocumentsForFinancialAccount(account.id),
        refetchOnWindowFocus: false,
    });

    const removeFinancialDocumentMutation =
        useRemoveFinancialDocumentMutation();

    const handleRemoveItem = useCallback(
        async (item: FinancialDocument) => {
            removeFinancialDocumentMutation.mutate({
                financialDocumentId: item.id,
                confirmTransactionsRemoval: true,
            });
        },
        [removeFinancialDocumentMutation],
    );

    const sortedDocuments = useMemo(() => {
        if (!data) {
            return undefined;
        }

        const getStatusPriority = (doc: FinancialDocument): number => {
            const importStatus = getImportStatus(doc);
            if (importStatus === "done") {
                return 0;
            }
            if (importStatus === "pending") {
                return 1;
            }

            const reconciliationStatus = getReconciliationStatus(doc);
            if (reconciliationStatus === "done") {
                return 2;
            }
            if (reconciliationStatus === "pending") {
                return 3;
            }

            return 4;
        };

        return [...data].sort(
            (a, b) => getStatusPriority(a) - getStatusPriority(b),
        );
    }, [data]);

    if (isError || !sortedDocuments) {
        return null;
    }

    if (sortedDocuments.length === 0) {
        return null;
    }

    return (
        <section className="account-details__section">
            <header className="account-details__section__header">
                <h4>Documents</h4>
            </header>

            <main className="account-details__section__content">
                <div className={styles.body}>
                    <ImportedDocuments
                        financialDocuments={sortedDocuments}
                        onRemoveItem={handleRemoveItem}
                    />
                </div>
            </main>
        </section>
    );
};
